import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/sonner';
import { OpsHeading, OpsState, errorText } from './OperationsUI';
import { RefreshCw, Loader2, Layers, Users, PackageSearch, ExternalLink, ShieldAlert, CircleCheck, Receipt } from 'lucide-react';

const TYPE_LABEL = { product: 'Productos', price: 'Precios', customer: 'Clientes', subscription: 'Suscripciones' };

export const PlatformStripeSync = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [objects, setObjects] = useState([]);
  const [objType, setObjType] = useState('product');
  const [busy, setBusy] = useState('');
  const [tax, setTax] = useState(null);
  const [taxSaving, setTaxSaving] = useState(false);

  const loadTax = useCallback(async () => {
    try { const { data } = await api.get('/platform/stripe/tax-status'); setTax(data); } catch { /* ignore */ }
  }, []);

  const loadStatus = useCallback(async () => {
    setLoading(true); setError('');
    try { const { data } = await api.get('/platform/stripe/status'); setStatus(data); }
    catch (e) { setError(errorText(e)); } finally { setLoading(false); }
  }, []);

  const loadObjects = useCallback(async (type) => {
    try { const { data } = await api.get('/platform/stripe/objects', { params: { object_type: type } }); setObjects(data.objects || []); }
    catch (e) { toast.error(errorText(e)); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadTax(); }, [loadTax]);
  useEffect(() => { loadObjects(objType); }, [objType, loadObjects]);

  const toggleTax = async (enabled) => {
    setTaxSaving(true);
    try { await api.put('/platform/stripe/tax-settings', { enabled }); await loadTax(); toast.success(enabled ? 'Impuesto automático activado en el checkout' : 'Impuesto automático desactivado'); }
    catch (e) { toast.error(errorText(e)); } finally { setTaxSaving(false); }
  };

  const run = async (key, path, label) => {
    setBusy(key);
    try {
      const { data } = await api.post(path);
      if (key === 'plans') toast.success(`Planes sincronizados (${data.synced?.length || 0})`);
      else if (key === 'customers') toast.success(`Clientes: ${data.created} creados, ${data.updated} actualizados`);
      else if (key === 'reconcile') toast.success(`Reconciliado · ${data.products}P / ${data.prices}Pr / ${data.customers}C / ${data.subscriptions}S`);
      else toast.success(`${label} completado`);
      loadStatus(); loadObjects(objType);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(''); }
  };

  return (
    <div data-testid="platform-stripe-sync-page">
      <OpsHeading title="Sincronización con Stripe" description="Crea y mantiene sincronizados los planes, clientes y productos con tu panel de Stripe. Los cambios hechos directamente en el Dashboard de Stripe se reflejan aquí mediante webhooks y la reconciliación.">
        <Button variant="outline" data-testid="stripe-reconcile" disabled={!!busy} onClick={() => run('reconcile', '/platform/stripe/reconcile')}>{busy === 'reconcile' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Reconciliar desde Stripe</Button>
      </OpsHeading>

      <OpsState loading={loading} error={error} reload={loadStatus}>
        {status && (
          <>
            {!status.keys_ready && (
              <Alert className="mb-5 border-brand-orange/40" data-testid="stripe-not-ready">
                <ShieldAlert className="h-4 w-4" />
                <AlertDescription>La cuenta de Stripe usa la clave de prueba compartida. Reclama tu sandbox en la pestaña <strong>Payments</strong> para poder crear entradas en tu propio Dashboard.</AlertDescription>
              </Alert>
            )}

            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
              <div className="seller-panel !mb-0" data-testid="stripe-status-account">
                <p className="seller-help">Cuenta Stripe</p>
                <p className="text-sm font-bold flex items-center gap-2 mt-1">{status.keys_ready ? <CircleCheck className="h-4 w-4 text-brand-sage" /> : <ShieldAlert className="h-4 w-4 text-brand-orange" />}<Badge variant="outline">{status.mode === 'test' ? 'Modo prueba' : 'Modo real'}</Badge></p>
                <p className="text-xs text-muted-foreground mt-2 break-all">{status.account?.id || '—'}{status.account?.country ? ` · ${status.account.country}` : ''}</p>
                <a href={status.dashboard_url} target="_blank" rel="noreferrer" className="text-xs text-brand-orange inline-flex items-center gap-1 mt-2 hover:underline" data-testid="stripe-dashboard-link">Abrir Dashboard <ExternalLink className="h-3 w-3" /></a>
              </div>
              {['product', 'customer', 'price'].map((t) => (
                <div className="seller-panel !mb-0" key={t} data-testid={`stripe-count-${t}`}>
                  <p className="seller-help">{TYPE_LABEL[t]} sincronizados</p>
                  <p className="text-2xl font-bold">{status.counts?.[t] ?? 0}</p>
                  {t === 'customer' && <p className="text-xs text-muted-foreground mt-1">{status.organizations?.synced}/{status.organizations?.total} organizaciones</p>}
                </div>
              ))}
            </section>

            <section className="seller-panel mb-6" data-testid="stripe-actions">
              <div className="seller-section-row"><h2>Sincronizar hacia Stripe</h2></div>
              <p className="seller-help mt-1">Estas acciones crean o actualizan entradas en tu Dashboard de Stripe de forma idempotente (no duplican objetos existentes).</p>
              <div className="mt-3 flex flex-wrap gap-3">
                <Button className="seller-primary" data-testid="stripe-sync-plans" disabled={!!busy || !status.keys_ready} onClick={() => run('plans', '/platform/stripe/sync/plans')}>{busy === 'plans' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />} Sincronizar planes de suscripción</Button>
                <Button variant="outline" data-testid="stripe-sync-customers" disabled={!!busy || !status.keys_ready} onClick={() => run('customers', '/platform/stripe/sync/customers')}>{busy === 'customers' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} Sincronizar clientes (organizaciones)</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-2"><PackageSearch className="h-3.5 w-3.5" /> Para productos individuales del catálogo usa el botón “Stripe” en <strong>Productos globales</strong>.</p>
            </section>

            <section className="seller-panel mb-6" data-testid="stripe-tax-panel">
              <div className="seller-section-row"><h2 className="flex items-center gap-2"><Receipt className="h-5 w-5" /> Impuestos automáticos (Stripe Tax)</h2>{tax && <Badge variant="outline" data-testid="stripe-tax-stripe-status">Stripe Tax: {tax.stripe_tax_status === 'active' ? 'Activo' : (tax.stripe_tax_status || 'sin configurar')}</Badge>}</div>
              <p className="seller-help mt-1">Al activarlo, el checkout de suscripción calcula y añade el impuesto correcto según la ubicación del cliente, y las facturas de Stripe lo reflejan.</p>
              <div className="mt-3 flex items-center justify-between rounded-lg border border-border p-3 max-w-xl">
                <Label htmlFor="stripe-tax-toggle" className="pr-4">Añadir impuesto automático en el checkout</Label>
                <Switch id="stripe-tax-toggle" data-testid="stripe-tax-toggle" disabled={taxSaving || !status.keys_ready} checked={!!tax?.enabled} onCheckedChange={toggleTax} />
              </div>
              {tax?.enabled && tax?.stripe_tax_status !== 'active' && (
                <Alert className="mt-3 border-brand-orange/40 max-w-xl" data-testid="stripe-tax-warning">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertDescription>Stripe Tax todavía no está configurado en tu cuenta. Configura la dirección de origen y los registros fiscales en el Dashboard para que el impuesto se aplique; mientras tanto el cobro se procesa sin impuesto.
                    <a href={tax.dashboard_url} target="_blank" rel="noreferrer" className="text-brand-orange inline-flex items-center gap-1 ml-1 hover:underline" data-testid="stripe-tax-config-link">Configurar Stripe Tax <ExternalLink className="h-3 w-3" /></a>
                  </AlertDescription>
                </Alert>
              )}
            </section>

            <section className="seller-panel" data-testid="stripe-objects-panel">
              <div className="seller-section-row"><h2>Objetos sincronizados</h2><Badge variant="outline">{objects.length}</Badge></div>
              <Tabs value={objType} onValueChange={setObjType} className="mt-3">
                <TabsList className="flex flex-wrap h-auto w-fit">{Object.keys(TYPE_LABEL).map((t) => <TabsTrigger key={t} value={t} data-testid={`stripe-tab-${t}`}>{TYPE_LABEL[t]}</TabsTrigger>)}</TabsList>
              </Tabs>
              <div className="seller-table-wrap mt-3">
                <Table>
                  <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Stripe ID</TableHead><TableHead>Origen</TableHead><TableHead>Vínculo local</TableHead><TableHead>Actualizado</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {objects.length ? objects.map((o) => (
                      <TableRow key={o.stripe_id} data-testid={`stripe-obj-${o.stripe_id}`}>
                        <TableCell className="font-medium max-w-56 truncate">{o.name || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground break-all">{o.stripe_id}</TableCell>
                        <TableCell><Badge variant="outline" data-testid={`stripe-obj-source-${o.stripe_id}`}>{o.source === 'dashboard' ? 'Dashboard' : 'Plataforma'}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{o.local_type ? `${o.local_type}${o.local_id ? `: ${String(o.local_id).slice(0, 12)}` : ''}` : '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{o.updated_at ? new Date(o.updated_at).toLocaleString('es') : '—'}</TableCell>
                      </TableRow>
                    )) : <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8" data-testid="stripe-objects-empty">Aún no hay {TYPE_LABEL[objType].toLowerCase()} sincronizados. Usa las acciones de arriba o reconcilia desde Stripe.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </section>
          </>
        )}
      </OpsState>
    </div>
  );
};
